import { useEffect, useState, useContext } from "react";
import axios from 'axios';

import { AppContext } from '../../../context.js';
import Helmet from "../../components/helmet.js";
import { Button } from '../../components/component.js';
import {
  US_STATES,
  DEFAULT_PLACE_OF_SERVICE,
  PLACE_OF_SERVICE_OPTIONS,
} from "../../../utils/constants.js";
import { sanitizeZip, isValidZip, ZIP_HINT } from "../../../utils/zip.js";

const GCODE_META = [
  { gCode: 'G0519', label: 'Low Complexity Dyad', group: 'new', defaultAmount: 157 },
  { gCode: 'G0520', label: 'Moderate Complexity Dyad', group: 'new', defaultAmount: 288 },
  { gCode: 'G0521', label: 'High Complexity Dyad', group: 'new', defaultAmount: 377 },
  { gCode: 'G0522', label: 'Low Complexity Individual', group: 'new', defaultAmount: 241 },
  { gCode: 'G0523', label: 'Moderate to High Complexity Individual', group: 'new', defaultAmount: 409 },
  { gCode: 'G0524', label: 'Low Complexity Dyad', group: 'est', defaultAmount: 68 },
  { gCode: 'G0525', label: 'Moderate Complexity Dyad', group: 'est', defaultAmount: 126 },
  { gCode: 'G0526', label: 'High Complexity Dyad', group: 'est', defaultAmount: 231 },
  { gCode: 'G0527', label: 'Low Complexity Individual', group: 'est', defaultAmount: 126 },
  { gCode: 'G0528', label: 'Moderate to High Complexity Individual', group: 'est', defaultAmount: 225 },
];

const DEFAULT_AMOUNTS = GCODE_META.map((m) => ({
  gCode: m.gCode,
  amount: m.defaultAmount.toFixed(2),
}));

const EMPTY_GENERAL = {
  title: '',
  npi: '',
  renderingProviderFirstName: '',
  renderingProviderLastName: '',
  renderingProviderNPI: '',
  facilityName: '',
  facilityAddress: '',
  facilityCity: '',
  facilityState: '',
  facilityZip: '',
  facilityNPI: '',
  billingProviderAddress: '',
  billingProviderCity: '',
  billingProviderState: '',
  billingProviderZip: '',
  billingProviderPhone: '',
  defaultPlaceOfService: DEFAULT_PLACE_OF_SERVICE,
};

function formatPhone(val) {
  const digits = String(val ?? "").replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function mapGeneralFromApi(data) {
  return {
    title: data.title || '',
    npi: data.npi || '',
    renderingProviderFirstName: data.renderingProviderFirstName || '',
    renderingProviderLastName: data.renderingProviderLastName || '',
    renderingProviderNPI: data.renderingProviderNPI || '',
    facilityName: data.facilityName || '',
    facilityAddress: data.facilityAddress || '',
    facilityCity: data.facilityCity || '',
    facilityState: data.facilityState || '',
    facilityZip: data.facilityZip || '',
    facilityNPI: data.facilityNPI || '',
    billingProviderAddress: data.billingProviderAddress || '',
    billingProviderCity: data.billingProviderCity || '',
    billingProviderState: data.billingProviderState || '',
    billingProviderZip: data.billingProviderZip || '',
    billingProviderPhone: data.billingProviderPhone || '',
    defaultPlaceOfService: data.defaultPlaceOfService || DEFAULT_PLACE_OF_SERVICE,
  };
}

function mapAmountsFromApi(existing) {
  const byCode = new Map(
    (existing || []).map((a) => [String(a.gCode || '').toUpperCase(), parseFloat(a.amount)])
  );
  const source = byCode.size > 0 ? byCode : new Map(DEFAULT_AMOUNTS.map((a) => [a.gCode, parseFloat(a.amount)]));
  return GCODE_META.map((m) => {
    const amt = source.has(m.gCode) ? source.get(m.gCode) : m.defaultAmount;
    return { gCode: m.gCode, amount: Number.isFinite(amt) ? amt.toFixed(2) : '' };
  });
}

function displayValue(val) {
  return val ? val : 'not set';
}

function placeOfServiceLabel(code) {
  return PLACE_OF_SERVICE_OPTIONS.find((o) => o.value === code)?.label ?? code ?? '';
}

function ClaimSettings() {
  const context = useContext(AppContext);

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('general');

  const [general, setGeneral] = useState({ ...EMPTY_GENERAL });
  const [savedGeneral, setSavedGeneral] = useState({ ...EMPTY_GENERAL });
  const [editingGeneral, setEditingGeneral] = useState(false);
  const [savingGeneral, setSavingGeneral] = useState(false);

  const [amounts, setAmounts] = useState([]);
  const [savedAmounts, setSavedAmounts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let u = context.addSpiner();
    axios.get('/api/admin/settings').then((response) => {
      const mappedGeneral = mapGeneralFromApi(response.data);
      setGeneral(mappedGeneral);
      setSavedGeneral(mappedGeneral);

      const mapped = mapAmountsFromApi(response.data.claimAmounts || []);
      setAmounts(mapped);
      setSavedAmounts(mapped);
      setLoading(false);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, []);

  function updateGeneralField(field, value) {
    setGeneral((prev) => ({ ...prev, [field]: value }));
  }

  function handleEditGeneral() {
    setGeneral({ ...savedGeneral });
    setEditingGeneral(true);
  }

  function handleCancelGeneral() {
    setGeneral({ ...savedGeneral });
    setEditingGeneral(false);
  }

  function handleSaveGeneral() {
    const facilityZipInvalid = !!general.facilityZip && !isValidZip(general.facilityZip);
    const billingZipInvalid = !!general.billingProviderZip && !isValidZip(general.billingProviderZip);
    if (facilityZipInvalid || billingZipInvalid) {
      context.addError({ message: ZIP_HINT });
      return;
    }

    // Send all fields as strings (including "") so cleared values are written.
    // Backend treats nil as "leave unchanged" and non-nil (including "") as write.
    const payload = {
      title: general.title,
      npi: general.npi,
      renderingProviderFirstName: general.renderingProviderFirstName,
      renderingProviderLastName: general.renderingProviderLastName,
      renderingProviderNPI: general.renderingProviderNPI,
      facilityName: general.facilityName,
      facilityAddress: general.facilityAddress,
      facilityCity: general.facilityCity,
      facilityState: general.facilityState,
      facilityZip: general.facilityZip,
      facilityNPI: general.facilityNPI,
      billingProviderAddress: general.billingProviderAddress,
      billingProviderCity: general.billingProviderCity,
      billingProviderState: general.billingProviderState,
      billingProviderZip: general.billingProviderZip,
      billingProviderPhone: general.billingProviderPhone,
      defaultPlaceOfService: general.defaultPlaceOfService,
    };

    setSavingGeneral(true);
    let u = context.addSpiner();
    axios.post('/api/admin/settings/update', payload).then(() => {
      const next = { ...general };
      setSavedGeneral(next);
      setGeneral(next);
      setEditingGeneral(false);
      context.addNotification && context.addNotification('Claim settings saved.');
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      setSavingGeneral(false);
      context.removeSpiner(u);
    });
  }

  function handleEdit() {
    setAmounts(savedAmounts.map((a) => ({ ...a })));
    setEditing(true);
  }

  function handleCancel() {
    setAmounts(savedAmounts.map((a) => ({ ...a })));
    setEditing(false);
  }

  function updateAmount(gCode, value) {
    setAmounts((prev) => prev.map((a) => (a.gCode === gCode ? { ...a, amount: value } : a)));
  }

  function handleAmountBlur(gCode, value) {
    if (value === '' || value === null || value === undefined) {
      updateAmount(gCode, '');
      return;
    }
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      updateAmount(gCode, parsed.toFixed(2));
    }
  }

  function handleSave() {
    const toSave = [];
    for (const row of amounts) {
      const trimmed = typeof row.amount === 'string' ? row.amount.trim() : String(row.amount ?? '');
      if (trimmed === '') {
        context.addError({ message: `Claim amount for ${row.gCode} is required.` });
        return;
      }
      const amt = parseFloat(trimmed);
      if (isNaN(amt) || amt <= 0) {
        context.addError({ message: `Claim amount for ${row.gCode} must be a valid positive number.` });
        return;
      }
      toSave.push({ gCode: row.gCode, amount: amt.toFixed(2) });
    }

    setSaving(true);
    let u = context.addSpiner();
    axios.post('/api/admin/settings/claimAmounts/update', {
      amounts: toSave.map((a) => ({ gCode: a.gCode, amount: parseFloat(a.amount) })),
    }).then(() => {
      setSavedAmounts(toSave.map((a) => ({ ...a })));
      setAmounts(toSave.map((a) => ({ ...a })));
      setEditing(false);
      context.addNotification && context.addNotification('Claim amounts saved.');
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      setSaving(false);
      context.removeSpiner(u);
    });
  }

  if (loading) return null;

  const displayGeneral = editingGeneral ? general : savedGeneral;
  const displayAmounts = editing ? amounts : savedAmounts;
  const byCode = new Map(displayAmounts.map((a) => [a.gCode, a.amount]));
  const facilityZipInvalid = !!displayGeneral.facilityZip && !isValidZip(displayGeneral.facilityZip);
  const billingZipInvalid = !!displayGeneral.billingProviderZip && !isValidZip(displayGeneral.billingProviderZip);

  function zipInputClass(invalid) {
    return "input sm typo-normal-150 w-100" + (invalid ? " border-red-500 bg-red-50" : "");
  }

  function renderGroup(groupTitle, groupKey) {
    const rows = GCODE_META.filter((m) => m.group === groupKey);
    return (
      <div className="card mb-3" key={groupKey}>
        <div className="card-header sm">
          <div className="typo-strong-150">{groupTitle}</div>
        </div>
        <div className="card-header sm">
          <div className="row typo-strong-150">
            <div className="col-xs-3">G-Code</div>
            <div className="col-xs-5">Description</div>
            <div className="col-xs-4">Claim Amount ($)</div>
          </div>
        </div>
        {rows.map((meta, i) => {
          const amount = byCode.has(meta.gCode) ? byCode.get(meta.gCode) : '';
          return (
            <div
              key={meta.gCode}
              className={"card-header sm " + (i === rows.length - 1 ? "last" : "")}
            >
              <div className="row align-items-center">
                <div className="col-xs-3">
                  <div className="typo-normal-150">{meta.gCode}</div>
                </div>
                <div className="col-xs-5">
                  <div className="typo-normal-150" style={{ color: '#666' }}>{meta.label}</div>
                </div>
                <div className="col-xs-4">
                  {editing ? (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input"
                      value={amount}
                      onChange={(e) => updateAmount(meta.gCode, e.target.value)}
                      onBlur={(e) => handleAmountBlur(meta.gCode, e.target.value)}
                      placeholder="e.g. 157.00"
                    />
                  ) : (
                    <div className="typo-normal-150">
                      {amount ? `$${amount}` : 'not set'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderTextField(label, field, opts = {}) {
    const { placeholder = '', className = 'input sm typo-normal-150 w-50', format } = opts;
    const value = displayGeneral[field] || '';
    return (
      <>
        <div className="typo-strong-125">{label}</div>
        {editingGeneral ? (
          <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => updateGeneralField(field, format ? format(e.target.value) : e.target.value)}
            className={className}
          />
        ) : (
          <div className="typo-normal-150">{displayValue(value)}</div>
        )}
      </>
    );
  }

  return (
    <>
      <Helmet title="Settings | Claim Settings" />

      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-xs-12 col-lg-offset-2 col-lg-8">
            <div className="typo-hero-small">Claim Settings</div>
            <div className="typo-normal-175">
              Configure default claim form fields and charge amounts. Saving also updates existing claim forms.
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-xs-12 col-lg-offset-2 col-lg-8">
            <div className="d-flex justify-content-start align-items-center">
              <div
                className={"item typo-normal-150 " + (tab === 'general' ? 'active' : '')}
                onClick={() => setTab('general')}
              >
                General
              </div>
              <div
                className={"item typo-normal-150 ms-2 " + (tab === 'amounts' ? 'active' : '')}
                onClick={() => setTab('amounts')}
              >
                Amounts
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12 col-lg-offset-2 col-lg-8">

            {tab === 'general' && (
              <>
                <div className="card mb-3">
                  <div className="card-header sm">
                    <div className="typo-strong-200">Service Facility</div>
                  </div>
                  <div className="card-content sm">
                    {renderTextField('Facility Name', 'facilityName', { placeholder: 'Facility name...' })}

                    <br />

                    {renderTextField('Address', 'facilityAddress', { placeholder: 'Address...', className: 'input sm typo-normal-150 w-100' })}

                    <br />

                    <div className="row">
                      <div className="col-xs-6">
                        <div className="typo-strong-125">City</div>
                        {editingGeneral ? (
                          <input type="text" placeholder="City..." value={displayGeneral.facilityCity} onChange={(e) => updateGeneralField('facilityCity', e.target.value)} className="input sm typo-normal-150 w-100" />
                        ) : (
                          <div className="typo-normal-150">{displayValue(displayGeneral.facilityCity)}</div>
                        )}
                      </div>
                      <div className="col-xs-3">
                        <div className="typo-strong-125">State</div>
                        {editingGeneral ? (
                          <select value={displayGeneral.facilityState} onChange={(e) => updateGeneralField('facilityState', e.target.value)} className="input sm typo-normal-150 w-100">
                            <option value="">Select...</option>
                            {US_STATES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        ) : (
                          <div className="typo-normal-150">{displayValue(displayGeneral.facilityState)}</div>
                        )}
                      </div>
                      <div className="col-xs-3">
                        <div className={"typo-strong-125" + (facilityZipInvalid ? " text-red-600" : "")}>ZIP</div>
                        {editingGeneral ? (
                          <>
                            <input type="text" placeholder="ZIP..." value={displayGeneral.facilityZip} onChange={(e) => updateGeneralField('facilityZip', sanitizeZip(e.target.value))} className={zipInputClass(facilityZipInvalid)} />
                            <div className={"typo-normal-125 mt-1 " + (facilityZipInvalid ? "text-red-600" : "text-muted")}>{ZIP_HINT}</div>
                          </>
                        ) : (
                          <div className={"typo-normal-150" + (facilityZipInvalid ? " text-red-600" : "")}>{displayValue(displayGeneral.facilityZip)}</div>
                        )}
                      </div>
                    </div>

                    <br />

                    {renderTextField('NPI', 'facilityNPI', { placeholder: 'NPI...' })}
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-header sm">
                    <div className="typo-strong-200">Place of Service</div>
                  </div>
                  <div className="card-content sm">
                    <div className="typo-strong-125">Default Place of Service</div>
                    {editingGeneral ? (
                      <select
                        value={displayGeneral.defaultPlaceOfService}
                        onChange={(e) => updateGeneralField('defaultPlaceOfService', e.target.value)}
                        className="input sm typo-normal-150 w-100"
                      >
                        {PLACE_OF_SERVICE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="typo-normal-150">{placeOfServiceLabel(displayGeneral.defaultPlaceOfService)}</div>
                    )}
                    <div className="typo-normal-125 text-muted mt-1">
                      Pre-fills the Place of Service on each patient's claim form. Saving a new default updates patients still on the previous default; patients set to a different code are left alone.
                    </div>
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-header sm">
                    <div className="typo-strong-200">Rendering Provider</div>
                  </div>
                  <div className="card-content sm">
                    <div className="row">
                      <div className="col-xs-6">
                        <div className="typo-strong-125">First Name</div>
                        {editingGeneral ? (
                          <input type="text" placeholder="First name..." value={displayGeneral.renderingProviderFirstName} onChange={(e) => updateGeneralField('renderingProviderFirstName', e.target.value)} className="input sm typo-normal-150 w-100" />
                        ) : (
                          <div className="typo-normal-150">{displayValue(displayGeneral.renderingProviderFirstName)}</div>
                        )}
                      </div>
                      <div className="col-xs-6">
                        <div className="typo-strong-125">Last Name</div>
                        {editingGeneral ? (
                          <input type="text" placeholder="Last name..." value={displayGeneral.renderingProviderLastName} onChange={(e) => updateGeneralField('renderingProviderLastName', e.target.value)} className="input sm typo-normal-150 w-100" />
                        ) : (
                          <div className="typo-normal-150">{displayValue(displayGeneral.renderingProviderLastName)}</div>
                        )}
                      </div>
                    </div>

                    <br />

                    {renderTextField('NPI', 'renderingProviderNPI', { placeholder: 'NPI...' })}
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-header sm">
                    <div className="typo-strong-200">Billing Provider</div>
                  </div>
                  <div className="card-content sm">
                    {renderTextField('Billing Provider Name', 'title', { placeholder: 'Billing provider name...' })}

                    <br />

                    {renderTextField('Address', 'billingProviderAddress', { placeholder: 'Address...', className: 'input sm typo-normal-150 w-100' })}

                    <br />

                    <div className="row">
                      <div className="col-xs-6">
                        <div className="typo-strong-125">City</div>
                        {editingGeneral ? (
                          <input type="text" placeholder="City..." value={displayGeneral.billingProviderCity} onChange={(e) => updateGeneralField('billingProviderCity', e.target.value)} className="input sm typo-normal-150 w-100" />
                        ) : (
                          <div className="typo-normal-150">{displayValue(displayGeneral.billingProviderCity)}</div>
                        )}
                      </div>
                      <div className="col-xs-3">
                        <div className="typo-strong-125">State</div>
                        {editingGeneral ? (
                          <select value={displayGeneral.billingProviderState} onChange={(e) => updateGeneralField('billingProviderState', e.target.value)} className="input sm typo-normal-150 w-100">
                            <option value="">Select...</option>
                            {US_STATES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        ) : (
                          <div className="typo-normal-150">{displayValue(displayGeneral.billingProviderState)}</div>
                        )}
                      </div>
                      <div className="col-xs-3">
                        <div className={"typo-strong-125" + (billingZipInvalid ? " text-red-600" : "")}>ZIP</div>
                        {editingGeneral ? (
                          <>
                            <input type="text" placeholder="ZIP..." value={displayGeneral.billingProviderZip} onChange={(e) => updateGeneralField('billingProviderZip', sanitizeZip(e.target.value))} className={zipInputClass(billingZipInvalid)} />
                            <div className={"typo-normal-125 mt-1 " + (billingZipInvalid ? "text-red-600" : "text-muted")}>{ZIP_HINT}</div>
                          </>
                        ) : (
                          <div className={"typo-normal-150" + (billingZipInvalid ? " text-red-600" : "")}>{displayValue(displayGeneral.billingProviderZip)}</div>
                        )}
                      </div>
                    </div>

                    <br />

                    {renderTextField('Phone', 'billingProviderPhone', { placeholder: 'xxx-xxx-xxxx', format: formatPhone })}

                    <br />

                    {renderTextField('NPI', 'npi', { placeholder: 'NPI...' })}
                  </div>
                </div>

                <div className="d-flex justify-content-end align-items-center mb-4">
                  {editingGeneral ? (
                    <>
                      <Button onClick={handleCancelGeneral} color="grey" text="Cancel" className="me-2" disabled={savingGeneral} />
                      <Button onClick={handleSaveGeneral} color="blue" text={savingGeneral ? "Saving…" : "Save"} disabled={savingGeneral} />
                    </>
                  ) : (
                    <Button onClick={handleEditGeneral} color="blue" text="Edit" />
                  )}
                </div>
              </>
            )}

            {tab === 'amounts' && (
              <>
                <div className="typo-normal-150 text-muted mb-3">
                  Default claim charge amount for each GUIDE G-code. Saving updates Total Charges on claim forms for patients with that G-code.
                </div>

                {renderGroup('First 6 Months (New Patient Payment Rate)', 'new')}
                {renderGroup('After First 6 Months (Established Patient Payment Rate)', 'est')}

                <div className="d-flex justify-content-end align-items-center mb-4">
                  {editing ? (
                    <>
                      <Button onClick={handleCancel} color="grey" text="Cancel" className="me-2" disabled={saving} />
                      <Button onClick={handleSave} color="blue" text={saving ? "Saving…" : "Save"} disabled={saving} />
                    </>
                  ) : (
                    <Button onClick={handleEdit} color="blue" text="Edit" />
                  )}
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </>
  );
}

export default ClaimSettings;
